import { useEffect, useState, type ReactNode } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { OnboardingModal } from '@/components/OnboardingModal';
import { useRoomStore } from '@/stores/roomStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi } from '@/auth/authApi';
import { containsProfanity } from '@shared/profanity';
import { roomApi } from '@/rooms/roomApi';
import { Avatar, Logo, RankMedal } from '@/art';
import { Pill } from '@/components/Pill';
import { LangSwitcher } from '@/components/LangSwitcher';
import { PingIndicator } from '@/components/PingIndicator';
import { useT } from '@/i18n/useI18n';
import { audio } from '@/audio/AudioService';
import { adsAvailable, showRewarded } from '@/ads/adService';
import { dailyApi } from '@/rewards/dailyApi';
import { DailyQuests } from '@/components/DailyQuests';
import { CoinIcon, GemIcon } from '@/components/CurrencyIcon';
import { useBossStore } from '@/stores/bossStore';

const PROFILE_EDIT_COST = 10;

import { STARS_PER_DIVISION } from '@shared/gameConfig';
import { useClanNotifStore } from '@/stores/clanNotifStore';
import { useClanStore } from '@/stores/clanStore';

const DIVISION_ROMAN = ['', 'I', 'II', 'III', 'IV'] as const;

export function HomeScreen() {
  const go = useUIStore((s) => s.go);
  const profile = useUserStore((s) => s.profile);
  const setRoom = useRoomStore((s) => s.setRoom);
  const clanUnread = useClanNotifStore((s) => s.unread);
  const t = useT();
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinErr, setJoinErr] = useState<string | null>(null);
  const [topErr, setTopErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const bossEnabled = useBossStore((s) => s.enabled);
  const loadBosses = useBossStore((s) => s.load);
  const clanEnabled = useClanStore((s) => s.enabled);
  const loadClanAvailability = useClanStore((s) => s.loadAvailability);

  // New-player onboarding: show once for accounts that haven't customized
  // their name yet (nameChangeCount 0) and haven't completed onboarding.
  const onboarded = useSettingsStore((s) => s.onboarded);
  const authUser = useAuthStore((s) => s.user);
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (!onboarded && authUser && (authUser.nameChangeCount ?? 0) === 0) {
      setShowOnboarding(true);
    }
  }, [onboarded, authUser]);

  async function handleCreate() {
    if (creating) return;
    setTopErr(null);
    setCreating(true);
    try {
      const r = await roomApi.create();
      setRoom(r.room);
      go('room');
    } catch (e: any) {
      setTopErr(e?.message ?? t('home.could_not_create_room'));
    }
    setCreating(false);
  }

  useEffect(() => {
    void loadBosses();
    void loadClanAvailability();
  }, [loadBosses, loadClanAvailability]);

  async function handleJoin() {
    setJoinErr(null);
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) { setJoinErr(t('home.code_too_short')); return; }
    try {
      const r = await roomApi.join(code);
      setRoom(r.room);
      setJoinOpen(false);
      setJoinCode('');
      go('room');
    } catch (e: any) {
      setJoinErr(e.message ?? t('home.could_not_join'));
    }
  }

  // Visible progression — Wild-Rift style stars within a division.
  const maxStars = STARS_PER_DIVISION[profile.rank] ?? 4;
  const stars = Math.max(0, Math.min(maxStars, profile.rankStars));
  const progressPct = (stars / maxStars) * 100;
  const divisionRoman = DIVISION_ROMAN[profile.rankDivision] ?? 'IV';

  return (
    <div className="screen screen-fit screen-fit--dense">
      <div className="hex-bg" />

      <div
        style={{
          padding: '12px 18px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0 }}>
          <button
            onClick={() => { audio.play('ui.tap'); setProfileOpen(true); }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%', lineHeight: 0, flexShrink: 0 }}
            aria-label={t('home.open_profile')}
          >
            <Avatar color={profile.avatarColor} size={44} photoUrl={profile.avatarUrl} characterUrl={profile.characterIdleUrl} frame={profile.frame} />
          </button>
          <div style={{ minWidth: 0 }}>
            <div
              className="display"
              style={{
                fontSize: 16,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {profile.name}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <PingIndicator />
          <LangSwitcher />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 'clamp(8px,2.5vh,24px)',
          marginBottom: 'clamp(6px,1.8vh,16px)',
        }}
      >
        <Logo scale={1} />
      </div>

      <div style={{ padding: '0 18px' }}>
        <div
          className="card"
          style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14 }}
        >
          <RankMedal rank={profile.rank} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 15, letterSpacing: '0.06em' }}>
              {profile.rank.toUpperCase()} {divisionRoman}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 3,
                marginTop: 4,
                marginBottom: 4,
                fontSize: 14,
              }}
            >
              {Array.from({ length: maxStars }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    color: i < stars ? 'var(--c-yellow)' : 'var(--ink-dim)',
                    textShadow: i < stars ? '0 0 6px rgba(244,199,36,0.7)' : 'none',
                    lineHeight: 1,
                  }}
                >
                  ★
                </span>
              ))}
            </div>
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, var(--c-yellow), #fff)',
                  boxShadow: 'var(--glow-yellow)',
                }}
              />
            </div>
            <div style={{ fontSize: 10, color: 'var(--ink-dim)', marginTop: 4 }}>
              {profile.mmr} MMR
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono" style={{ fontSize: 9, color: 'var(--ink-dim)' }}>
              {t('home.win_streak')}
            </div>
            <div
              className="display"
              style={{
                fontSize: 22,
                color: 'var(--c-cyan)',
                textShadow: '0 0 10px rgba(34,211,238,0.5)',
              }}
            >
              {profile.winStreak}
            </div>
          </div>
        </div>
      </div>

      {/* Main account/shop navigation. Social and daily game modes live in the
          lower activity nav so this row stays compact on small phones. */}
      <div
        style={{
          padding: 'clamp(8px,1.6vh,14px) 18px 0',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 8,
        }}
      >
        <NavTile icon={<ShopIcon />} label={t('tab.shop')} color="var(--c-yellow)" onClick={() => go('shop')} />
        <NavTile icon={<BagIcon />} label={t('tab.bag')} color="var(--c-cyan)" onClick={() => go('inventory')} />
        <NavTile icon={<RankIcon />} label={t('tab.ranks')} color="var(--c-purple)" onClick={() => go('leaderboard')} />
        <NavTile icon={<SettingsIcon />} label={t('tab.settings')} color="var(--ink-muted)" onClick={() => go('settings')} />
      </div>

      <div
        style={{
          padding: 'clamp(8px,1.8vh,18px) 18px 8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 8,
        }}
      >
        <DailyAdButton />
        <button
          className="btn btn-primary"
          onClick={() => { audio.play('ui.tap_primary'); go('matching'); }}
          style={{ fontSize: 'clamp(20px,5vw,24px)', padding: 'clamp(12px,2.2vh,18px)' }}
        >
          {t('home.quick_match')}
        </button>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            className="btn"
            onClick={handleCreate}
            disabled={creating}
            style={{
              fontSize: 13,
              padding: '12px',
              background: 'rgba(244,199,36,0.15)',
              border: '1px solid var(--c-yellow)',
              color: 'var(--c-yellow)',
            }}
          >
            {t('home.create_room')}
          </button>
          <button
            className="btn"
            onClick={() => { setJoinErr(null); setJoinOpen(true); }}
            style={{
              fontSize: 13,
              padding: '12px',
              background: 'rgba(34,211,238,0.15)',
              border: '1px solid var(--c-cyan)',
              color: 'var(--c-cyan)',
            }}
          >
            {t('home.join_room')}
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            color: 'var(--ink-dim)',
            padding: '0 4px',
          }}
        >
          <span>{t('home.modes_subtitle')}</span>
          <span>{t('home.avg_wait')}</span>
        </div>
        {topErr && (
          <div
            onClick={() => setTopErr(null)}
            style={{
              fontSize: 11,
              color: 'var(--c-red)',
              border: '1px solid rgba(230,57,70,0.4)',
              background: 'rgba(230,57,70,0.08)',
              borderRadius: 6,
              padding: '8px 10px',
              textAlign: 'center',
            }}
          >
            ⚠ {topErr}
          </div>
        )}
      </div>

      {joinOpen && (
        <JoinModal
          code={joinCode}
          err={joinErr}
          onChange={(c) => setJoinCode(c.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
          onCancel={() => { setJoinOpen(false); setJoinCode(''); setJoinErr(null); }}
          onConfirm={handleJoin}
        />
      )}

      {profileOpen && <ProfilePopup onClose={() => setProfileOpen(false)} />}
      {showOnboarding && <OnboardingModal onDone={() => setShowOnboarding(false)} />}

      <div
        className="home-activity-nav"
        style={{
          gridTemplateColumns: `repeat(${1 + Number(clanEnabled) + Number(bossEnabled)}, minmax(0, 1fr))`,
        }}
      >
        {clanEnabled && (
          <NavTile
            icon={<NavImage src="/assets/home-nav/clan.png" />}
            label={t('clan.title')}
            color="var(--c-blue)"
            onClick={() => go('clan')}
            badge={clanUnread > 0}
            visual="image"
          />
        )}
        {bossEnabled && (
          <NavTile
            icon={<NavImage src="/assets/home-nav/boss.png" />}
            label={t('boss.title')}
            color="var(--c-red)"
            onClick={() => {
              audio.play('ui.tap_primary');
              go('bossSelect');
            }}
            visual="image"
          />
        )}
        <DailyQuests variant="nav" />
      </div>
    </div>
  );
}

/** Once-a-day rewarded-ad bonus banner. Native only; hides once claimed/used. */
function DailyAdButton() {
  const t = useT();
  const setProfile = useUserStore((s) => s.setProfile);
  const [status, setStatus] = useState<{ available: boolean; gems: number; coins: number } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adsAvailable()) return;
    dailyApi.status().then(setStatus).catch(() => { /* offline / not logged in */ });
  }, []);

  if (!adsAvailable() || !status?.available) return null;

  async function claim() {
    if (busy) return;
    setBusy(true);
    try {
      const earned = await showRewarded();
      if (!earned) { setBusy(false); return; }
      const r = await dailyApi.claim();
      setProfile({ coins: r.balanceCoins, gems: r.balanceGems });
      audio.play('ui.coin_pickup');
      setStatus((s) => (s ? { ...s, available: false } : s));
    } catch {
      audio.play('ui.error');
    }
    setBusy(false);
  }

  return (
    <button
      onClick={claim}
      disabled={busy}
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: 12,
        border: '1px solid var(--c-purple)',
        background: 'linear-gradient(135deg, rgba(155,93,229,0.18), rgba(155,93,229,0.34))',
        color: 'var(--c-purple)',
        fontFamily: 'var(--f-display)',
        fontSize: 13,
        letterSpacing: '0.04em',
        cursor: busy ? 'default' : 'pointer',
        boxShadow: '0 0 16px rgba(155,93,229,0.3)',
      }}
    >
      {busy ? (
        t('home.daily_ad_watching')
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          📺 {t('home.daily_ad')} {status.gems}
          <GemIcon size={14} /> +{status.coins}
          <CoinIcon size={14} />
        </span>
      )}
    </button>
  );
}

interface NavTileProps {
  icon: ReactNode;
  label: string;
  color: string;
  onClick: () => void;
  visual?: 'icon' | 'image';
  /** Show a small red attention dot in the corner (e.g. unread clan news). */
  badge?: boolean;
}

function NavTile({
  icon,
  label,
  color,
  onClick,
  visual = 'icon',
  badge = false,
}: NavTileProps) {
  const isImage = visual === 'image';
  return (
    <button
      onClick={onClick}
      className="card"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        minWidth: 0,
        minHeight: isImage ? 72 : undefined,
        padding: isImage ? '6px 4px 7px' : '10px 4px',
        background: 'rgba(0,0,0,0.25)',
        border: `1px solid ${color}40`,
        cursor: 'pointer',
      }}
    >
      {badge && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: 'var(--c-red)',
            boxShadow: '0 0 0 2px rgba(0,0,0,0.35), 0 0 6px var(--c-red)',
          }}
        />
      )}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: isImage ? 40 : 24,
          color,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          maxWidth: '100%',
          fontSize: 9,
          lineHeight: 1.1,
          color: 'var(--ink-muted)',
          letterSpacing: '0.06em',
          fontFamily: 'var(--f-display)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </button>
  );
}

function NavImage({ src }: { src: string }) {
  return <img aria-hidden className="home-nav-image" src={src} alt="" />;
}

// Nav icons — line-art glyphs that read clearly at 22px. stroke=currentColor so
// each tile colours its own icon via the wrapper span.
const NAV_ICON = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function ShopIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M4 9.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5" />
      <path d="M3 6.5h18l-1.2 3.2a2.4 2.4 0 0 1-4.5 0 2.4 2.4 0 0 1-4.6 0 2.4 2.4 0 0 1-4.5 0z" />
      <path d="M9.5 20v-5.5h5V20" />
    </svg>
  );
}

function BagIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M6.5 8.5a5.5 5.5 0 0 1 11 0V19a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1z" />
      <path d="M9.5 8.5a2.5 2.5 0 0 1 5 0" />
      <rect x="9.5" y="12.5" width="5" height="4.5" rx="1" />
    </svg>
  );
}

function RankIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M7 4h10v4.5a5 5 0 0 1-10 0z" />
      <path d="M7 5H4.5v1.5a3 3 0 0 0 3 3" />
      <path d="M17 5h2.5v1.5a3 3 0 0 1-3 3" />
      <path d="M12 13.5V16" />
      <path d="M9 20h6l-.7-2.5h-4.6z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg {...NAV_ICON}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 13a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

interface JoinModalProps {
  code: string;
  err: string | null;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

function JoinModal({ code, err, onChange, onCancel, onConfirm }: JoinModalProps) {
  const t = useT();
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: 'max(env(safe-area-inset-top, 0px), 18px) 18px max(env(safe-area-inset-bottom, 0px), 18px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 280, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div className="display" style={{ fontSize: 14, color: 'var(--c-cyan)', letterSpacing: '0.1em' }}>
          {t('home.join_room_title')}
        </div>
        <input
          autoFocus
          value={code}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onConfirm()}
          placeholder="ABCD12"
          maxLength={6}
          style={{
            fontFamily: 'var(--f-display)',
            fontSize: 22,
            letterSpacing: '0.18em',
            padding: 12,
            textAlign: 'center',
            background: 'rgba(0,0,0,0.4)',
            border: '1px solid var(--hairline)',
            borderRadius: 8,
            color: 'var(--c-yellow)',
            textTransform: 'uppercase',
          }}
        />
        {err && <div style={{ color: 'var(--c-red)', fontSize: 11, textAlign: 'center' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onCancel}>{t('common.cancel')}</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onConfirm}>{t('home.join_btn')}</button>
        </div>
      </div>
    </div>
  );
}

function ProfilePopup({ onClose }: { onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);
  const profile = useUserStore((s) => s.profile);
  const setProfile = useUserStore((s) => s.setProfile);
  const t = useT();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: 'max(env(safe-area-inset-top, 0px), 18px) 18px max(env(safe-area-inset-bottom, 0px), 18px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 320, maxWidth: '100%', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            color={profile.avatarColor}
            size={52}
            photoUrl={profile.avatarUrl}
            characterUrl={profile.characterIdleUrl}
            frame={profile.frame}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="display" style={{ fontSize: 16 }}>{profile.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: 2 }}>
              {profile.rank.toUpperCase()} · {profile.mmr} MMR
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
            <Pill icon="gem">{profile.gems}</Pill>
          </div>
        </div>

        <ProfileField
          label={t('home.display_name')}
          value={user?.displayName ?? ''}
          placeholder={t('home.display_name_ph')}
          changeCount={user?.nameChangeCount ?? 0}
          gems={profile.gems}
          allowEmpty={false}
          save={async (next) => {
            if (!token) return;
            const { user: fresh } = await authApi.updateDisplayName(token, next);
            setUser(fresh);
            setProfile({ name: fresh.displayName, gems: fresh.gems, coins: fresh.coins });
          }}
        />

        <ProfileField
          label={t('home.victory_shout')}
          value={user?.victoryShout ?? ''}
          placeholder={t('home.victory_shout_ph')}
          changeCount={user?.shoutChangeCount ?? 0}
          gems={profile.gems}
          allowEmpty
          save={async (next) => {
            if (!token) return;
            const { user: fresh } = await authApi.updateVictoryShout(token, next);
            setUser(fresh);
            setProfile({ gems: fresh.gems, coins: fresh.coins });
          }}
        />

        <button className="btn btn-ghost" onClick={onClose} style={{ marginTop: 2 }}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

interface ProfileFieldProps {
  label: string;
  value: string;
  placeholder: string;
  changeCount: number;
  gems: number;
  allowEmpty: boolean;
  save: (next: string) => Promise<void>;
}

function ProfileField({ label, value, placeholder, changeCount, gems, allowEmpty, save }: ProfileFieldProps) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cost = changeCount > 0 ? PROFILE_EDIT_COST : 0;
  const trimmed = draft.trim();
  const dirty = trimmed !== value && (allowEmpty || trimmed.length > 0);
  const canAfford = cost === 0 || gems >= cost;
  const hasProfanity = trimmed.length > 0 && containsProfanity(trimmed);

  const open = () => { setDraft(value); setErr(null); setEditing(true); };
  const cancel = () => { setEditing(false); setErr(null); };

  const onSave = async () => {
    if (!dirty || busy || !canAfford) return;
    if (hasProfanity) { setErr(t('profanity.blocked')); return; }
    setBusy(true);
    setErr(null);
    try {
      await save(trimmed);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>{label}</span>
        <span style={{ fontSize: 10, color: cost === 0 ? 'var(--c-cyan)' : 'var(--c-yellow)' }}>
          {cost === 0 ? t('home.first_change_free') : `${cost} 💎`}
        </span>
      </div>

      {!editing ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: value ? 'var(--ink)' : 'var(--ink-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value || t('home.field_none')}
          </span>
          <button className="btn btn-ghost" onClick={open} style={{ fontSize: 10, padding: '6px 12px', color: 'var(--c-yellow)', borderColor: 'rgba(244,199,36,0.4)' }}>
            {t('common.edit')}
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              autoFocus
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setErr(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') void onSave(); if (e.key === 'Escape') cancel(); }}
              maxLength={24}
              placeholder={placeholder}
              style={{
                flex: 1,
                background: 'rgba(0,0,0,0.35)',
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                padding: '8px 10px',
                color: 'var(--ink)',
                fontFamily: 'var(--f-body)',
                fontSize: 13,
                outline: 'none',
                minWidth: 0,
              }}
            />
            <button
              onClick={onSave}
              disabled={!dirty || busy || !canAfford || hasProfanity}
              className="btn btn-ghost"
              style={{
                fontSize: 10,
                padding: '6px 12px',
                opacity: !dirty || busy || !canAfford || hasProfanity ? 0.4 : 1,
                color: 'var(--c-cyan)',
                borderColor: 'rgba(34,211,238,0.4)',
              }}
            >
              {busy ? '…' : cost > 0 ? t('home.save_cost', { cost }) : t('common.save')}
            </button>
            <button onClick={cancel} className="btn btn-ghost" style={{ fontSize: 10, padding: '6px 10px' }}>
              ✕
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--ink-dim)', display: 'flex', justifyContent: 'space-between' }}>
            <span>{cost > 0 && !canAfford ? t('home.not_enough_gems') : t('home.max_chars', { n: 24 })}</span>
            <span>{draft.length}/24</span>
          </div>
          {hasProfanity && (
            <div style={{ fontSize: 11, color: 'var(--c-red)' }}>{t('profanity.blocked')}</div>
          )}
        </>
      )}
      {err && <div style={{ fontSize: 11, color: 'var(--c-red)' }}>{err}</div>}
    </div>
  );
}
